import {
  Map,
  type MapLayerEventType,
  type EventData,
  type MapEventType,
} from "mapbox-gl";
import { type Ref, inject, provide, watch, onBeforeUnmount, ref } from "vue";

const MAP_BOUNDS_SYMBOL = Symbol("map bounds");
const MAP_SYMBOL = Symbol("map");
const MAP_ZOOM_SYMBOL = Symbol("map zoom");
const MAP_CENTER_SYMBOL = Symbol("center");
const MAP_DRAGGING_SYMBOL = Symbol("map dragging");

export function provideZoom(zoom: Ref<number>) {
  provide(MAP_ZOOM_SYMBOL, zoom);
}

export function useZoom(): Ref<number> {
  const zoom = inject(MAP_ZOOM_SYMBOL) as Ref<number> | undefined;
  if (!zoom) throw new Error("failed to find map zoom");
  return zoom;
}

export function useMetersToPixels(): (
  meters: number,
  latitude?: number
) => number {
  const zoom = useZoom();
  const center = useMapCenter();

  return (meters: number, latitude?: number): number => {
    if (!latitude && !center.value) {
      console.error("Could not find map center.");
      return NaN;
    }

    const lat = latitude || center.value.lat;
    if (!lat) return NaN;

    const pixelsPerMeter =
      (78271.484 / 2 ** zoom.value / Math.cos((lat * Math.PI) / 180)) * 0.5;

    return meters / pixelsPerMeter;
  };
}

export function usePixelsToMeters(): (
  pixels: number,
  latitude?: number
) => number {
  const zoom = useZoom();
  const center = useMapCenter();

  return (pixels: number, latitude?: number): number => {
    if (!latitude && !center.value) {
      console.error("Could not find map center.");
      return NaN;
    }

    const lat = latitude || center.value?.lat;
    if (!lat) return NaN;
    const pixelsPerMeter =
      (78271.484 / 2 ** zoom.value / Math.cos((lat * Math.PI) / 180)) * 0.5;

    return pixels * pixelsPerMeter;
  };
}

export function provideMapBounds(
  bounds: Ref<{
    north: number;
    south: number;
    east: number;
    west: number;
  }>
) {
  provide(MAP_BOUNDS_SYMBOL, bounds);
}

export function useMapBounds(): Ref<{
  north: number;
  south: number;
  east: number;
  west: number;
}> {
  return inject(MAP_BOUNDS_SYMBOL);
}

export function provideMap(map) {
  provide(MAP_SYMBOL, map);
}

export function useMap(): Ref<Map | null> {
  return inject(MAP_SYMBOL, ref(null));
}

export function useMapCenter(): Ref<{ lat: number; lng: number }> {
  return inject(MAP_CENTER_SYMBOL);
}

export function provideMapCenter(
  center: Ref<{ lat: number; lng: number } | null>
) {
  return provide(MAP_CENTER_SYMBOL, center);
}

export function useMapOn<T extends keyof MapLayerEventType>(
  type: T,
  layer: string | ReadonlyArray<string>,
  listener: (ev: MapLayerEventType[T] & EventData) => void
): void;

export function useMapOn<T extends keyof MapEventType>(
  type: T,
  listener: (ev: MapEventType[T] & EventData) => void
): void;

export function useMapOn(...params: any[]) {
  const map = useMap();

  watch(
    map,
    (newMap, oldMap) => {
      if (oldMap && typeof oldMap.off == "function")
        oldMap.off.apply(oldMap, params);
      if (newMap && typeof newMap.on == "function") {
        newMap.on.apply(newMap, params);
      }
    },
    { immediate: true }
  );

  onBeforeUnmount(() => map.value?.off.apply(map.value, params));
}

export function useMapDragging(): Ref<boolean> {
  return inject(MAP_DRAGGING_SYMBOL);
}

export function provideMapDragging(dragging: Ref<boolean>) {
  return provide(MAP_DRAGGING_SYMBOL, dragging);
}

export function mapPaintProperty<T>(
  r: Ref<T>,
  layer: string,
  property: string
) {
  const map = useMap();
  const apply = () => {
    const m = map.value;
    if (!m || !m.getLayer(layer)) return;
    m.setPaintProperty(layer, property, r.value as any);
  };
  watch([r, map], apply, { immediate: true });
  // Re-apply after a setStyle replaces the underlying style — without
  // this, callers' state (e.g. settings.showSatellite) wouldn't restore
  // when basemap switches recreate the layer.
  useMapOn("styledata", apply);
}

export function mapLayoutProperty<T>(
  r: Ref<T>,
  layer: string,
  property: string
) {
  const map = useMap();
  const apply = () => {
    const m = map.value;
    if (!m || !m.getLayer(layer)) return;
    if (property == "visibility") {
      m.setLayoutProperty(
        layer,
        property,
        (r.value as unknown) ? "visible" : "none"
      );
    } else m.setLayoutProperty(layer, property, r.value as any);
  };
  watch([r, map], apply, { immediate: true });
  useMapOn("styledata", apply);
}
